import { useCallback, useEffect, useMemo, useState } from 'react'
import { useApplyPrompt } from '../../../hooks/useApplyPrompt'
import { useGetPrompts } from '../../../hooks/useGetPrompts'
import type { Prompt } from '../../../types'

// Strip HTML tags to get the raw text the user actually typed.
function getPlaintext(html: string): string {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent ?? ''
}

/**
 * Drives the slash-command prompt menu inside the chat editor.
 *
 * When the editor content contains a `/`, we show a filterable list of favourite
 * prompts. Keyboard navigation (ArrowUp / ArrowDown / Enter / Escape) and mouse
 * clicks are both handled here; callers only need to render the returned state
 * and forward editor keydown events to `onEditorKeyDown`.
 *
 * @returns `true` from `onEditorKeyDown` when the event was fully handled
 * (caller should not let it propagate to send logic).
 */
export function usePromptSlashMenu({
  message,
  onAfterSelect,
}: {
  message: string
  onAfterSelect?: () => void
}) {
  const { data } = useGetPrompts()

  const favorites = useMemo(() => data?.favorites ?? [], [data])
  const plaintext = useMemo(() => getPlaintext(message), [message])

  const hasSlash = plaintext.startsWith('/')
  const query = hasSlash ? plaintext.slice(1) : ''

  const { applyPrompt } = useApplyPrompt({
    fetchDocumentName: favorites.some((prompt) => prompt.requiresFullDocument),
  })

  const filtered = useMemo(() => {
    if (!hasSlash) return []
    if (query === '') return favorites
    const q = query.toLowerCase()
    return favorites.filter((prompt) => prompt.title.toLowerCase().includes(q))
  }, [hasSlash, query, favorites])

  const hasNoFavorites = favorites.length === 0

  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isDismissed, setIsDismissed] = useState(false)

  // Re-open and reset highlight whenever the editor text changes
  useEffect(() => {
    setHighlightedIndex(0)
    setIsDismissed(false)
  }, [plaintext])

  const isOpen = hasSlash && !isDismissed && (hasNoFavorites || filtered.length > 0)

  const handleSelect = useCallback(
    (prompt: Prompt) => {
      if (!isOpen) return
      applyPrompt(prompt)
      // Called after applyPrompt — gives the caller a chance to move the cursor to the end
      // once the new content is in the editor.
      onAfterSelect?.()
      setIsDismissed(true)
    },
    [isOpen, onAfterSelect, applyPrompt]
  )

  /**
   * Call this with every keydown event from the editor.
   * Returns `true` if the event was consumed (caller must not send the message).
   */
  const onEditorKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      if (!isOpen) return false

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setHighlightedIndex((i) => (filtered.length > 0 ? (i + 1) % filtered.length : 0))
          return false

        case 'ArrowUp':
          e.preventDefault()
          setHighlightedIndex((i) =>
            filtered.length > 0 ? (i - 1 + filtered.length) % filtered.length : 0
          )
          return false

        case 'Enter': {
          e.preventDefault()
          e.stopPropagation()
          // guard against highlightedIndex being out of range after the filtered list shrinks
          const selected = filtered[highlightedIndex] ?? filtered[0]
          if (selected) {
            handleSelect(selected)
          } else {
            setIsDismissed(true)
          }
          return true
        }

        case 'Escape':
          e.preventDefault()
          setIsDismissed(true)
          return true

        default:
          return false
      }
    },
    [isOpen, filtered, highlightedIndex, handleSelect]
  )

  return {
    isOpen,
    filtered,
    hasNoFavorites,
    highlightedIndex,
    handleSelect,
    onEditorKeyDown,
  }
}
